import { useState, useEffect, useCallback } from 'react';

// RESUMEN DEL NEGOCIO y FICHA DE UN BOLICHE (super-admin).
//
// El Resumen contesta "¿cómo viene TukiPro?" y "¿a quién tengo que atender hoy?". La
// ficha junta en un lugar todo lo de un boliche; las acciones (bonificar, precio, cargos,
// titular) siguen siendo las de SuperAdmin.jsx y se piden por props.

const PLAN_LABEL = { basico: 'Básico', pro: 'Pro', full: 'Full' };
const ROL_LABEL = { owner: 'Dueño', admin: 'Admin', socio: 'Socio', rrpp: 'RRPP', tpv: 'Caja' };
const MES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function money(n) {
  if (n == null) return '—';
  return '$' + Math.round(Number(n)).toLocaleString('es-AR');
}
// Fechas de día ('YYYY-MM-DD') se leen como día, sin pasar por la zona horaria.
function dia(d) {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  const [y, m, dd] = s.split('-');
  return y && m && dd ? `${dd}/${m}/${y.slice(2)}` : '—';
}
function fecha(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' }); } catch { return '—'; }
}
// "hace 3 días", para lo que importa por lo reciente.
function hace(d) {
  if (!d) return 'nunca';
  const min = Math.round((Date.now() - new Date(d)) / 60000);
  if (min < 60) return 'hace un rato';
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const dd = Math.round(h / 24);
  if (dd < 60) return `hace ${dd} día${dd === 1 ? '' : 's'}`;
  return `hace ${Math.round(dd / 30)} meses`;
}
function variacion(act, ant) {
  if (!ant) return null;
  const p = Math.round(100 * (act - ant) / ant);
  return { txt: (p > 0 ? '+' : '') + p + '% vs. los mismos días del mes anterior', color: p >= 0 ? '#2ecc71' : 'var(--red-400)' };
}

function Tile({ label, value, sub, subColor, color }) {
  return (
    <div className="lb-card" style={{ padding: '16px 18px' }}>
      <div className="lb-eyebrow">{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, color: color || '#fff', marginTop: 4 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: subColor || 'var(--ink-600)', marginTop: 4, lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

function Seccion({ titulo, children, extra }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div className="lb-h" style={{ fontSize: 15, color: '#fff' }}>{titulo}</div>
        {extra}
      </div>
      {children}
    </div>
  );
}

const grilla = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px,1fr))', gap: 12 };

// Cómo se lee cada motivo de "Para atender".
const MOTIVOS = {
  aviso_pago: { label: 'Avisó que pagó', color: '#d4af37', txt: a => `${money(a.monto)} · avisó el ${dia(a.fecha)}. Fijate si entró la plata.` },
  moroso: { label: 'Moroso', color: 'var(--red-400)', txt: a => `Debe ${money(a.monto)} · vencido desde el ${dia(a.fecha)}` },
  prueba_vence: { label: 'Prueba por terminar', color: '#5b9bd5', txt: a => `La prueba termina el ${dia(a.fecha)}` },
  prueba_terminada: { label: 'Prueba terminada', color: '#5b9bd5', txt: a => `Terminó el ${dia(a.fecha)} y no eligió plan pago` },
  sin_arrancar: { label: 'Trabado en el arranque', color: '#EC268F', txt: a => `Se dio de alta el ${dia(a.fecha)}. Le falta: ${(a.faltan || []).join(', ')}` },
  sin_actividad: { label: 'En riesgo de baja', color: '#d4af37', txt: a => a.fecha ? `Paga, pero su último evento fue el ${dia(a.fecha)}` : 'Paga, pero nunca hizo un evento' },
};

// Alertas del vigía (backend/src/vigia.js): lo que se rompió y ya te llegó por email.
function Alertas({ alertas, api, onAbrir }) {
  const [probando, setProbando] = useState(false);
  const abiertas = alertas.filter(a => !a.resuelta_at);
  const resueltas = alertas.filter(a => a.resuelta_at);

  async function probar() {
    setProbando(true);
    try {
      const r = await api.post('/api/superadmin/alertas/prueba', {});
      alert(r.sin_envio ? 'El servidor no tiene configurado el envío de emails (RESEND_API_KEY).' : 'Listo. Fijate en tu casilla (y en spam) que haya llegado "Prueba de alertas de TukiPro".');
    } catch (e) { alert(e.message); } finally { setProbando(false); }
  }

  return (
    <Seccion titulo="Alertas" extra={<button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={probando} onClick={probar}>{probando ? 'Mandando…' : 'Probar el email de alertas'}</button>}>
      {abiertas.length === 0 ? (
        <div className="lb-card" style={{ padding: '14px 20px', fontSize: 13, color: '#2ecc71' }}>
          Todo funcionando.{resueltas.length > 0 && <span style={{ color: 'var(--ink-600)' }}> {resueltas.length} problema{resueltas.length === 1 ? '' : 's'} resuelto{resueltas.length === 1 ? '' : 's'} en el último día.</span>}
        </div>
      ) : (
        <div className="lb-card" style={{ padding: '4px 20px' }}>
          {abiertas.map(a => {
            const color = a.nivel === 'urgente' ? 'var(--red-400)' : '#d4af37';
            return (
              <div key={a.clave} style={{ padding: '12px 0', borderTop: '1px solid var(--line)', display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color }}>{a.nivel === 'urgente' ? 'URGENTE · ' : ''}{a.titulo}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 3, lineHeight: 1.5, wordBreak: 'break-word' }}>{a.detalle}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 3 }}>Desde {hace(a.primera_vez)}{a.avisado_at ? ' · te avisamos por email' : ''}</div>
                </div>
                {a.org_id && <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => onAbrir(a.org_id)}>Ver ficha</button>}
              </div>
            );
          })}
        </div>
      )}
    </Seccion>
  );
}

export function ResumenNegocio({ api, onAbrir }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [filtro, setFiltro] = useState(null);

  const cargar = useCallback(() => {
    setErr('');
    api.get('/api/superadmin/resumen').then(setD).catch(e => setErr(e.message));
  }, []); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  if (err) return <div className="lb-err">{err}</div>;
  if (!d) return <div style={{ color: 'var(--ink-600)', fontSize: 13, padding: '20px 0' }}>Cargando…</div>;

  const { boliches: b, abono_mensual: am, plata: p, entradas: en } = d;
  const vEnt = variacion(en.mes, en.mes_ant);
  const conteo = d.atender.reduce((acc, a) => ({ ...acc, [a.motivo]: (acc[a.motivo] || 0) + 1 }), {});
  const lista = filtro ? d.atender.filter(a => a.motivo === filtro) : d.atender;
  const maxEnt = Math.max(1, ...d.meses.map(m => m.entradas));
  const maxPlata = Math.max(1, ...d.meses.map(m => Math.max(m.facturado, m.cobrado)));

  return (
    <>
      <Alertas alertas={d.alertas || []} api={api} onAbrir={onAbrir} />

      <Seccion titulo="Plata">
        <div style={grilla}>
          <Tile label="Abono mensual" value={money(am.neto)} color="#EC268F"
            sub={`${am.pro} Pro · ${am.full} Full${am.lleno > am.neto ? ` · ${money(am.lleno - am.neto)} bonificado` : ''}`} />
          <Tile label="Facturado este mes" value={money(p.qr_mes + p.abonos_mes)}
            sub={`${money(p.qr_mes)} por entradas · ${money(p.abonos_mes)} de abonos`} />
          <Tile label="Cobrado este mes" value={money(p.cobrado_mes)} color="#2ecc71" />
          <Tile label="Te deben" value={money(p.adeudado)} color={p.adeudado > p.sin_vencer ? 'var(--red-400)' : '#fff'}
            sub={p.adeudado > 0 ? `${money(p.sin_vencer)} sin vencer` : 'Nadie debe nada'} />
        </div>
        {p.adeudado > p.sin_vencer && (
          <div className="lb-card" style={{ padding: '12px 18px', marginTop: 12, display: 'flex', gap: 22, flexWrap: 'wrap', fontSize: 13 }}>
            <span style={{ color: 'var(--ink-600)' }}>Deuda vencida:</span>
            <span>hasta 30 días <b style={{ color: '#d4af37' }}>{money(p.venc_30)}</b></span>
            <span>31 a 60 días <b style={{ color: 'var(--red-400)' }}>{money(p.venc_60)}</b></span>
            <span>más de 60 días <b style={{ color: 'var(--red-400)' }}>{money(p.venc_mas)}</b></span>
          </div>
        )}
      </Seccion>

      <Seccion titulo="Boliches y entradas">
        <div style={grilla}>
          <Tile label="Pagando" value={b.pagando} color="#2ecc71" sub={`${b.en_prueba} en prueba · ${b.exentos} exento${b.exentos === 1 ? '' : 's'}${b.suspendidos ? ` · ${b.suspendidos} suspendido${b.suspendidos === 1 ? '' : 's'}` : ''}`} />
          <Tile label="Altas este mes" value={b.altas_mes} sub={`${b.altas_mes_ant} el mes anterior`} />
          <Tile label="Pasan a pagar" value={b.conversion_pct == null ? '—' : b.conversion_pct + '%'}
            sub={b.terminaron_prueba ? `${b.convirtieron} de ${b.terminaron_prueba} que terminaron la prueba` : 'Nadie terminó la prueba todavía'} />
          <Tile label="Entradas este mes" value={en.mes.toLocaleString('es-AR')} sub={vEnt ? vEnt.txt : `Vendido por los boliches: ${money(en.vendido_mes)}`} subColor={vEnt?.color} />
        </div>
      </Seccion>

      <Seccion titulo="Para atender" extra={filtro && <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setFiltro(null)}>Ver todos</button>}>
        {d.atender.length === 0 ? (
          <div className="lb-card" style={{ padding: '18px 20px', fontSize: 13, color: 'var(--ink-600)' }}>Nada pendiente. Todos al día y andando.</div>
        ) : (<>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {Object.entries(MOTIVOS).filter(([k]) => conteo[k]).map(([k, m]) => (
              <button key={k} className="lb-btn lb-btn--sm" onClick={() => setFiltro(f => f === k ? null : k)}
                style={{ background: filtro === k ? m.color : 'transparent', color: filtro === k ? '#000' : m.color, border: `1px solid ${m.color}` }}>
                {m.label} · {conteo[k]}
              </button>
            ))}
          </div>
          <div className="lb-card" style={{ padding: '4px 20px' }}>
            {lista.map(a => {
              const m = MOTIVOS[a.motivo] || { label: a.motivo, color: '#fff', txt: () => '' };
              return (
                <div key={a.id + a.motivo} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderTop: '1px solid var(--line)', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{a.name} <span style={{ fontSize: 11, fontWeight: 700, color: m.color, marginLeft: 6 }}>{m.label}</span></div>
                    <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 2 }}>{m.txt(a)}</div>
                  </div>
                  <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => onAbrir(a.id)}>Ver ficha</button>
                </div>
              );
            })}
          </div>
        </>)}
      </Seccion>

      <Seccion titulo="Últimos 6 meses">
        <div className="lb-card tk-scroll-x" style={{ padding: '6px 20px' }}>
          <div className="lb-row lb-th" style={{ gridTemplateColumns: '0.7fr 1.6fr 1.6fr 1.6fr 0.6fr' }}>
            <span>Mes</span><span>Entradas</span><span>Facturado</span><span>Cobrado</span><span>Altas</span>
          </div>
          {d.meses.map(m => {
            const [y, mm] = m.mes.split('-');
            const barra = (v, max, color, txt) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, maxWidth: 110, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)' }}>
                  <div style={{ width: `${Math.round(100 * v / max)}%`, height: '100%', borderRadius: 3, background: color }} />
                </div>
                <span style={{ fontSize: 12, color: '#fff', whiteSpace: 'nowrap' }}>{txt}</span>
              </div>
            );
            return (
              <div key={m.mes} className="lb-row" style={{ gridTemplateColumns: '0.7fr 1.6fr 1.6fr 1.6fr 0.6fr', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--ink-700)' }}>{MES[Number(mm) - 1]} {y.slice(2)}</span>
                {barra(m.entradas, maxEnt, '#EC268F', m.entradas.toLocaleString('es-AR'))}
                {barra(m.facturado, maxPlata, '#5b9bd5', money(m.facturado))}
                {barra(m.cobrado, maxPlata, '#2ecc71', money(m.cobrado))}
                <span style={{ fontSize: 13, color: '#fff' }}>{m.altas}</span>
              </div>
            );
          })}
        </div>
      </Seccion>
    </>
  );
}

function Fila({ k, v, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13 }}>
      <span style={{ color: 'var(--ink-700)' }}>{k}</span>
      <span style={{ color: color || '#fff', fontWeight: 600, textAlign: 'right' }}>{v}</span>
    </div>
  );
}
function Bloque({ titulo, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="lb-eyebrow" style={{ marginBottom: 6 }}>{titulo}</div>
      {children}
    </div>
  );
}
const ok = (si, txtSi, txtNo) => <span style={{ color: si ? '#2ecc71' : '#d4af37' }}>{si ? txtSi : txtNo}</span>;

// acciones: { bonificar(org), precio(org), cargos(org), titular(org) } — las de SuperAdmin.
export function FichaBoliche({ orgId, api, onClose, acciones, version }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [nota, setNota] = useState('');
  const [busy, setBusy] = useState(false);

  const cargar = useCallback(() => {
    api.get(`/api/superadmin/orgs/${orgId}/ficha`).then(setD).catch(e => setErr(e.message));
  }, [orgId]); // eslint-disable-line
  // `version` cambia cuando algo de afuera (bonificar, marcar pagado) modificó el boliche.
  useEffect(() => { cargar(); }, [cargar, version]);

  async function agregarNota() {
    if (!nota.trim()) return;
    setBusy(true);
    try { await api.post(`/api/superadmin/orgs/${orgId}/notas`, { texto: nota }); setNota(''); cargar(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function borrarNota(id) {
    if (!window.confirm('¿Borrar esta nota?')) return;
    try { await api.del(`/api/superadmin/orgs/${orgId}/notas/${id}`); cargar(); } catch (e) { alert(e.message); }
  }

  const o = d?.org;
  const situacion = !o ? null
    : o.status === 'suspended' ? { t: 'Suspendido', c: 'var(--red-400)' }
    : !o.plan ? { t: 'Exento (no paga)', c: 'var(--ink-700)' }
    : d.plata.vencido > 0 ? { t: 'Moroso', c: 'var(--red-400)' }
    : d.prueba ? { t: 'En prueba', c: '#5b9bd5' }
    : { t: 'Al día', c: '#2ecc71' };

  return (
    <div className="lb-modal-backdrop" onClick={onClose}>
      <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 760, padding: 26, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div style={{ minWidth: 0 }}>
            <div className="lb-eyebrow" style={{ color: '#EC268F' }}>Ficha del boliche</div>
            <div className="lb-h" style={{ fontSize: 20, color: '#fff' }}>{o?.name || '…'}</div>
            {o && <div style={{ fontSize: 12, color: 'var(--ink-600)', wordBreak: 'break-word' }}>
              <a href={`https://${o.custom_domain || o.slug + '.tukipro.com'}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ink-700)' }}>{o.custom_domain || `${o.slug}.tukipro.com`}</a>
              {' · '}{o.owner_email || 'sin email'}{o.whatsapp ? ` · WhatsApp ${o.whatsapp}` : ''}
            </div>}
          </div>
          <button className="lb-iconbtn" onClick={onClose}>✕</button>
        </div>

        {err && <div className="lb-err">{err}</div>}
        {!d && !err && <div style={{ color: 'var(--ink-600)', fontSize: 13, padding: '14px 0' }}>Cargando…</div>}

        {d && (<>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
            {o.plan && <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => acciones.bonificar(o)}>Bonificar</button>}
            <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => acciones.precio(o)}>Precio</button>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => acciones.cargos(o)}>Ver cargos</button>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => acciones.titular(o)}>
              Datos del titular{d.titular_faltan > 0 ? ` (faltan ${d.titular_faltan})` : ''}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: '0 28px' }}>
            <div>
              <Bloque titulo="Cuenta">
                <Fila k="Situación" v={situacion.t} color={situacion.c} />
                <Fila k="Plan" v={o.plan ? `${PLAN_LABEL[o.plan]}${o.plan_price > 0 ? ` · ${money(o.plan_price)}/mes` : ' · solo entradas'}` : 'Exento'} />
                {o.bonif_pct != null && <Fila k="Bonificación" v={`${Number(o.bonif_pct)}% ${o.bonif_hasta ? 'hasta el ' + fecha(o.bonif_hasta) : 'para siempre'}`} color="#2ecc71" />}
                {o.plan_pending && <Fila k="Cambio programado" v={`Pasa a ${PLAN_LABEL[o.plan_pending]} el ${fecha(o.plan_pending_at)}`} color="#d4af37" />}
                {d.prueba && <Fila k="Prueba" v={`Termina el ${fecha(d.prueba.termina)} · ${d.prueba.qr_usados} de ${d.prueba.qr_limite} entradas`} color="#5b9bd5" />}
                <Fila k="Cliente desde" v={fecha(o.created_at)} />
                {o.paid_since && <Fila k="Paga desde" v={fecha(o.paid_since)} />}
              </Bloque>

              <Bloque titulo="Plata">
                <Fila k="Debe" v={money(d.plata.adeudado)} color={d.plata.vencido > 0 ? 'var(--red-400)' : d.plata.adeudado > 0 ? '#d4af37' : '#2ecc71'} />
                {d.plata.vencido > 0 && <Fila k="De eso, vencido" v={money(d.plata.vencido)} color="var(--red-400)" />}
                {d.plata.aviso_pago && <Fila k="Avisó que pagó" v="Sí, falta confirmar" color="#d4af37" />}
                <Fila k="Pagó en total" v={money(d.plata.cobrado_total)} />
                {d.plata.bonificado_total > 0 && <Fila k="Bonificado en total" v={money(d.plata.bonificado_total)} />}
                <Fila k="Último pago" v={fecha(d.plata.ultimo_pago)} />
              </Bloque>

              <Bloque titulo="Conexiones">
                <Fila k="Mercado Pago" v={o.mp_reconectar ? ok(false, '', 'Hay que reconectar') : ok(o.mp_conectado, 'Conectado', 'Sin conectar')} />
                <Fila k="Facturación ARCA" v={o.arca_enabled && o.arca_verificado_at ? ok(true, `Activa · punto de venta ${o.arca_punto_venta || '—'}`) : o.arca_certificado ? ok(false, '', 'Cargada, sin verificar') : <span style={{ color: 'var(--ink-600)' }}>No usa</span>} />
              </Bloque>
            </div>

            <div>
              <Bloque titulo="Actividad">
                <Fila k="Último ingreso al panel" v={hace(d.actividad.ultimo_ingreso_panel)} color={!d.actividad.ultimo_ingreso_panel ? 'var(--ink-600)' : undefined} />
                <Fila k="Última venta" v={hace(d.actividad.ultima_venta)} color={!d.actividad.ultima_venta ? 'var(--ink-600)' : undefined} />
                <Fila k="Próximo evento" v={dia(d.actividad.proximo_evento)} />
                <Fila k="Último evento" v={dia(d.actividad.ultimo_evento)} />
                <Fila k="Eventos" v={d.actividad.eventos} />
                <Fila k="Entradas vendidas" v={`${d.actividad.entradas.toLocaleString('es-AR')} · ${d.actividad.entradas_30d.toLocaleString('es-AR')} en 30 días`} />
              </Bloque>

              <Bloque titulo="Equipo">
                {d.usuarios.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-600)' }}>Sin usuarios activos.</div>}
                {d.usuarios.map(u => <Fila key={u.role} k={`${ROL_LABEL[u.role] || u.role} (${u.n})`} v={u.ultimo ? 'entró ' + hace(u.ultimo) : 'sin datos'} color={u.ultimo ? undefined : 'var(--ink-600)'} />)}
              </Bloque>

              {d.errores.length > 0 && (
                <Bloque titulo="Errores (últimos 7 días)">
                  {d.errores.map((e, i) => (
                    <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 12 }}>
                      <span style={{ color: '#d4af37', fontWeight: 700 }}>{e.source} · {e.kind}</span>
                      <span style={{ color: 'var(--ink-600)' }}> · {e.veces} {e.veces === 1 ? 'vez' : 'veces'} · {hace(e.ultimo)}</span>
                      {e.message && <div style={{ color: 'var(--ink-700)', marginTop: 2, wordBreak: 'break-word' }}>{e.message.slice(0, 140)}</div>}
                    </div>
                  ))}
                </Bloque>
              )}
            </div>
          </div>

          {d.eventos.length > 0 && (
            <Bloque titulo="Últimos eventos">
              <div className="tk-scroll-x">
                {d.eventos.map(e => (
                  <div key={e.id} style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13, alignItems: 'baseline', minWidth: 420 }}>
                    <span style={{ color: 'var(--ink-700)', width: 64, flexShrink: 0 }}>{dia(e.date)}</span>
                    <span style={{ color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
                    <span style={{ color: 'var(--ink-700)', whiteSpace: 'nowrap' }}>{e.vendidas} vendidas · {e.ingresaron} ingresaron</span>
                    <span style={{ whiteSpace: 'nowrap', width: 120, textAlign: 'right', color: e.cargo_estado === 'paid' ? '#2ecc71' : e.cargo_estado === 'bonificado' ? '#5b9bd5' : e.cargo_estado ? '#d4af37' : 'var(--ink-600)' }}>
                      {e.cargo_estado ? `${money(e.cargo)} · ${e.cargo_estado === 'paid' ? 'pagado' : e.cargo_estado === 'bonificado' ? 'bonificado' : 'impago'}` : 'sin cargo'}
                    </span>
                  </div>
                ))}
              </div>
            </Bloque>
          )}

          <Bloque titulo="Notas internas (el boliche no las ve)">
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input className="lb-input" value={nota} onChange={e => setNota(e.target.value)} maxLength={2000}
                placeholder="Ej: hablé con el dueño, paga el viernes" onKeyDown={e => { if (e.key === 'Enter') agregarNota(); }} />
              <button className="lb-btn lb-btn--primary lb-btn--sm" disabled={busy || !nota.trim()} onClick={agregarNota}>Agregar</button>
            </div>
            {d.notas.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>Todavía no hay notas.</div>}
            {d.notas.map(n => (
              <div key={n.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#fff', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.texto}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>{fecha(n.created_at)}{n.autor ? ` · ${n.autor}` : ''}</div>
                </div>
                <button className="lb-iconbtn" title="Borrar nota" onClick={() => borrarNota(n.id)}>✕</button>
              </div>
            ))}
          </Bloque>
        </>)}
      </div>
    </div>
  );
}
